import { BEEF_AI_ACCESS_TOKEN, beefAiGet } from "../common/utils/beefAiClient";
import { getCountFromBeefAiAggregateRow } from "../routes/pricing-reports/productSnapshotChartUtils";
import {
  PRICE_HISTORY_BREAKDOWN_GPS_PRESENT_JSON_FIELD,
  PRICE_HISTORY_BREAKDOWN_RAW_GPS_JSON_FIELD,
  applyPriceHistoryDimensionFilters,
  applyPriceHistoryQualityFilters,
  normalizePriceHistoryBreakdownRow,
} from "./beefAiPricing";

export const VERIFY_PRICE_PAGE_FIELDS = [
  "id",
  "record_at",
  "date_created",
  "product_id",
  "product_name",
  "raw_product_name",
  "retailer",
  "retailer_ref_id",
  "province",
  "provider",
  "price",
  "unit_price",
  "unit",
  "unit_quantity",
  "images",
  "human_verified",
  "is_void",
  "revision_status",
  "revision_metadata",
  PRICE_HISTORY_BREAKDOWN_GPS_PRESENT_JSON_FIELD,
  PRICE_HISTORY_BREAKDOWN_RAW_GPS_JSON_FIELD,
];

export const DEFAULT_VERIFY_PRICE_SORT = "-record_at,-date_created";

const VERIFY_PRICE_SCAN_FIELDS = [
  "id",
  "product_id",
  "unit_price",
  "price",
  "unit_quantity",
  "record_at",
  "human_verified",
];

const VERIFY_PRICE_SCAN_PAGE_SIZE = 500;
const VERIFY_PRICE_SCAN_MAX_PAGES = 100;
const VERIFY_PRICE_ID_CHUNK_SIZE = 80;

/**
 * @param {URLSearchParams} params
 * @param {"verified" | "unverified" | "both" | "voided"} view
 */
export function applyHumanVerifiedViewFilter(params, view) {
  if (view === "verified") {
    params.set("filter[human_verified][_eq]", "true");
    return;
  }

  if (view === "unverified") {
    params.set("filter[_and][2][_or][0][human_verified][_null]", "true");
    params.set("filter[_and][2][_or][1][human_verified][_eq]", "false");
  }
}

function accessToken() {
  return BEEF_AI_ACCESS_TOKEN;
}

function appendPageFields(params) {
  VERIFY_PRICE_PAGE_FIELDS.forEach((fieldName) => {
    params.append("fields[]", fieldName);
  });
}

function applyVerifyPriceBaseFilters(params, filters = {}) {
  applyPriceHistoryQualityFilters(params, filters);
  applyPriceHistoryDimensionFilters(params, filters, { includeDate: true });
}

function isVerifiedAggregateRow(row) {
  const value = row?.human_verified;
  return value === true || value === 1 || value === "1" || value === "true";
}

/**
 * @param {object[]} aggregateRows
 * @returns {{ verifiedCount: number, unverifiedCount: number, totalCount: number }}
 */
export function mapVerifyPriceCounts(aggregateRows) {
  let verifiedCount = 0;
  let unverifiedCount = 0;

  for (const row of aggregateRows || []) {
    const count = getCountFromBeefAiAggregateRow(row);
    if (isVerifiedAggregateRow(row)) {
      verifiedCount += count;
    } else {
      unverifiedCount += count;
    }
  }

  return {
    verifiedCount,
    unverifiedCount,
    totalCount: verifiedCount + unverifiedCount,
  };
}

/**
 * Verified / unverified totals for the current dimension filters (not the view toggle).
 *
 * @param {object} filters
 */
export async function fetchVerifyPriceCounts(filters = {}) {
  const params = new URLSearchParams();
  params.set("access_token", accessToken());
  params.set("aggregate[count]", "*");
  params.append("groupBy[]", "human_verified");
  params.set("limit", "-1");
  applyVerifyPriceBaseFilters(params, filters);

  const json = await beefAiGet(`/items/price_history?${params.toString()}`);
  return mapVerifyPriceCounts(json.data || []);
}

/**
 * Paginated individual `price_history` rows for the Verify Price card grid.
 *
 * @param {object} filters
 * @param {{
 *   page?: number,
 *   limit?: number,
 *   sort?: string,
 *   humanVerifiedView?: "verified" | "unverified" | "both" | "voided",
 * }} [pagination]
 */
export async function fetchVerifyPricePage(filters = {}, pagination = {}) {
  const limit = pagination.limit != null ? pagination.limit : 24;
  const page = pagination.page != null ? pagination.page : 1;
  const sort = pagination.sort || DEFAULT_VERIFY_PRICE_SORT;
  const humanVerifiedView = pagination.humanVerifiedView || "unverified";

  const params = new URLSearchParams();
  params.set("access_token", accessToken());
  params.set("limit", String(limit));
  params.set("page", String(page));
  params.set("sort", sort);
  params.append("meta[]", "filter_count");
  appendPageFields(params);
  applyVerifyPriceBaseFilters(params, {
    ...filters,
    voidedOnly: humanVerifiedView === "voided",
  });
  applyHumanVerifiedViewFilter(params, humanVerifiedView);

  const json = await beefAiGet(`/items/price_history?${params.toString()}`);
  const rawRows = json.data || [];
  const filterCountFromMeta = typeof json.meta?.filter_count === "number";
  const totalCount = filterCountFromMeta
    ? json.meta.filter_count
    : (page - 1) * limit + rawRows.length;

  return {
    rows: rawRows.map(normalizePriceHistoryBreakdownRow),
    totalCount,
  };
}

/**
 * Compact `price_history` rows for anomaly baselines (dimension filters only).
 * Includes verified and unverified rows so product week/month averages stay stable.
 *
 * @param {object} filters
 */
export async function fetchVerifyPriceScanRows(filters = {}) {
  const allRows = [];

  for (let page = 1; page <= VERIFY_PRICE_SCAN_MAX_PAGES; page += 1) {
    const params = new URLSearchParams();
    params.set("access_token", accessToken());
    params.set("limit", String(VERIFY_PRICE_SCAN_PAGE_SIZE));
    params.set("page", String(page));
    params.set("sort", "id");
    VERIFY_PRICE_SCAN_FIELDS.forEach((fieldName) => {
      params.append("fields[]", fieldName);
    });
    applyVerifyPriceBaseFilters(params, filters);

    const json = await beefAiGet(`/items/price_history?${params.toString()}`);
    const batch = json.data || [];
    allRows.push(...batch);

    if (!batch.length || batch.length < VERIFY_PRICE_SCAN_PAGE_SIZE) {
      break;
    }
  }

  return allRows;
}

/**
 * Full display rows for a set of `price_history` ids.
 *
 * @param {string[]} ids
 */
export async function fetchVerifyPriceRowsByIds(ids = []) {
  const uniqueIds = [...new Set((ids || []).filter(Boolean).map(String))];
  if (!uniqueIds.length) return [];

  const rows = [];

  for (let offset = 0; offset < uniqueIds.length; offset += VERIFY_PRICE_ID_CHUNK_SIZE) {
    const chunk = uniqueIds.slice(offset, offset + VERIFY_PRICE_ID_CHUNK_SIZE);
    const params = new URLSearchParams();
    params.set("access_token", accessToken());
    params.set("limit", String(chunk.length));
    params.set("filter[id][_in]", chunk.join(","));
    appendPageFields(params);

    const json = await beefAiGet(`/items/price_history?${params.toString()}`);
    rows.push(...(json.data || []).map(normalizePriceHistoryBreakdownRow));
  }

  const rowById = new Map(rows.map((row) => [String(row.id), row]));
  return uniqueIds.map((id) => rowById.get(id)).filter(Boolean);
}
